import { ScrollArea } from "@volca/ui/components/ui/scroll-area";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import {
  DetailPanelHeader,
  DetailSidebarFallback,
  DocumentPreviewSection,
  LineItemsSection,
  ReviewBadge,
  StatusBadge,
} from "@/accounts-payable/common";
import { AgentActionsSection } from "@/accounts-payable/invoice-detail/agent-actions-section";
import { InvoiceActivitySection } from "@/accounts-payable/invoice-detail/invoice-activity-section";
import {
  useGetDocumentAgentRuns,
  useGetReceipt,
} from "@/accounts-payable/query";
import {
  getActiveRun,
  getLatestCompletedRun,
  invoiceStatusMeta,
  reviewStateMeta,
  runsInFlightRefetchInterval,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";
import { ReceiptFieldsSection } from "./receipt-fields-section";

/** Side panel for one receipt: its fields, the document, and agent actions. */
export const ApReceiptDetail = ({
  receiptId,
  businessId,
  onClose,
  onViewAgentReview,
}: {
  receiptId: string;
  businessId: string | null;
  onClose: () => void;
  onViewAgentReview?: (receiptId: string) => void;
}) => {
  const { data: receipt, isPending } = useGetReceipt(businessId, receiptId);
  const {
    data: runs = [],
    isLoading: runsLoading,
    isError: runsError,
  } = useGetDocumentAgentRuns(
    businessId,
    "receipt",
    receiptId,
    runsInFlightRefetchInterval,
  );

  if (!receipt) {
    return (
      <DetailSidebarFallback
        title="Receipt"
        loading={isPending}
        errorMessage="Couldn’t load the receipt. Please try again."
        onClose={onClose}
      />
    );
  }

  return (
    <Stack align="start" gap={0} className="h-full w-full">
      {/* The purchase date stays in the fields, with its time. */}
      <DetailPanelHeader
        title={receipt.vendor_name}
        facts={[
          receipt.receipt_number ?? "Receipt",
          receipt.total_amount_cents ? (
            <span
              className={cn("font-semibold tabular-nums", {
                "text-danger": receipt.total_amount_cents < 0,
                "text-ink": receipt.total_amount_cents >= 0,
              })}
            >
              {formatCentsOrDashIfZero(receipt.total_amount_cents)}
            </span>
          ) : null,
        ]}
        statuses={
          <>
            <StatusBadge meta={invoiceStatusMeta(receipt)} />
            {reviewStateMeta(receipt.review_state) && (
              <ReviewBadge document={receipt} />
            )}
          </>
        }
        onClose={onClose}
      />

      <ScrollArea className="min-h-0 w-full flex-1">
        <Stack align="start" gap={8} className="w-full px-5 py-6">
          <ReceiptFieldsSection receipt={receipt} businessId={businessId} />
          {receipt.receipt_items.length > 0 && (
            <LineItemsSection
              items={receipt.receipt_items}
              businessId={businessId}
              documentId={receipt.id}
              documentType="receipt"
              agentRunId={receipt.agent_run_id}
            />
          )}
          <DocumentPreviewSection document={receipt} />
          <AgentActionsSection
            key={receipt.id}
            document={receipt}
            run={getLatestCompletedRun(runs)}
            activeRun={getActiveRun(runs)}
            loading={runsLoading}
            error={runsError}
            onViewAgentReview={(id) => {
              onClose();
              onViewAgentReview?.(id);
            }}
          />
          <InvoiceActivitySection invoice={receipt} />
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
